/**
 * Current-flight authority — what the desk, companion, and Megan agree is
 * being flown.
 *
 * Emerson doctrine (highest first):
 *   1. User click + Megan Mark Active / Activate / Accept. Sticks with zero
 *      SimConnect / SI. Clear / Undo is the only silent-free escape.
 *   2. Live sim multi-signal jury when present (track, closing/opening
 *      fields, callsign+STD, OFP). Signals abstain if missing/stale.
 *      Quorum is 2+; conflict prefers live sim.
 *   3. Schedule / undirected geometry last. Never invent a reverse pair.
 *
 * Do not hard-code one brittle source. Do not thrash every tick.
 */
import { resolveCoords } from "./airports";
import { icaoKey } from "./curfew";
import { haversineNm, initialTrueTrack } from "./geo";
import {
  isOpenOpsTicket,
  isSimTruthLifecycle,
  pickCurrentTicket,
  ticketLifecycle,
  type PickCurrentTicketInput,
} from "./opsBoard";
import type { FusedPosition } from "./position";
import { isHumanTicketAuthority, type TicketStatusAuthority } from "./ticketComplete";
import { jurySessionDecision } from "./flightSessions";
import type { CompanyProfile, Mission, SceneryAirport } from "./types";

export const TRACK_AGREE_DEG = 70;
export const CLOSING_MIN_NM = 8;
/** On-ground spawn this far from last parking is Narnia — geometry abstains. */
export const NARNIA_SPAWN_NM = 25;

export type FlightBindingAuthority = TicketStatusAuthority;

export type ActiveFlightBinding = {
  activeFlightId: string;
  activeFlightAuthority: FlightBindingAuthority | undefined;
  activeFlightAt: string;
  activeFlightClearedAt: string;
};

export type BindingSignalKind = "track" | "closing" | "callsignStd" | "ofp";
export type BindingVote = "match" | "mismatch" | "abstain";

export type BindingSignal = {
  kind: BindingSignalKind;
  vote: BindingVote;
  missionId?: string;
};

export type CurrentFlightReason = "human" | "jury" | "last_confident" | "schedule" | "pending";

/** Jury output is a suggestion. It is not allowed to open or rebind a session. */
export function sessionOpForResolution(resolution: Pick<CurrentFlightResolution, "reason" | "mission">): "none" | "suggest" {
  const decision = jurySessionDecision();
  if (decision.open || decision.rebind) return "none";
  if (resolution.reason === "jury" && resolution.mission) return "suggest";
  return "none";
}

export type CurrentFlightResolution = {
  mission: Mission | null;
  reason: CurrentFlightReason;
  confidence: "human" | "jury" | "hold" | "guess" | "none";
  signals: BindingSignal[];
};

export type ResolveCurrentFlightInput = PickCurrentTicketInput & {
  fused?: FusedPosition | null;
  previousFused?: FusedPosition | null;
  scenery?: ReadonlyArray<SceneryAirport>;
  si?: { originIcao?: string; destIcao?: string; callsign?: string; callsignIcao?: string } | null;
  now?: Date;
  activeFlightId?: string | null;
  activeFlightAuthority?: FlightBindingAuthority | null;
  lastConfidentId?: string | null;
  /** Last completed arrival / tail park — ops truth when the sim spawns elsewhere. */
  lastParkedIcao?: string | null;
  /** Optional sim-route score from flightGrounding. Positive = this ticket agrees. */
  scoreTicket?: (mission: Mission) => number;
};

function sameIcao(a: string | null | undefined, b: string | null | undefined): boolean {
  const left = icaoKey(a);
  const right = icaoKey(b);
  return Boolean(left) && left === right;
}

function normCall(raw: string | null | undefined): string {
  return String(raw ?? "")
    .trim()
    .toUpperCase()
    .replace(/[\s._-]+/g, "");
}

function headingDeltaDeg(a: number, b: number): number {
  return Math.abs(((a - b + 540) % 360) - 180);
}

function liveTrackDeg(fused?: FusedPosition | null): number | null {
  if (!fused) return null;
  if (typeof fused.trackDeg === "number" && Number.isFinite(fused.trackDeg)) return fused.trackDeg;
  if (typeof fused.heading === "number" && Number.isFinite(fused.heading)) return fused.heading;
  return null;
}

function fusedReady(fused?: FusedPosition | null): boolean {
  return Boolean(fused && !fused.stale && Number.isFinite(fused.lat) && Number.isFinite(fused.lon));
}

/**
 * Cold-boot / SimConnect drop spawn away from last parking. Geometry must
 * abstain — do not teleport or invent a reverse pair from garbage coords.
 */
export function isNarniaGroundSpawn(
  fused: FusedPosition | null | undefined,
  lastParkedIcao?: string | null,
  scenery?: ReadonlyArray<SceneryAirport>,
): boolean {
  if (!fusedReady(fused) || !fused) return false;
  if (fused.onGround === false) return false;
  const parked = icaoKey(lastParkedIcao);
  if (!parked) return false;
  const coords = resolveCoords(parked, [...(scenery ?? [])]);
  if (!coords) return true;
  const nm = haversineNm(fused.lat, fused.lon, coords.lat, coords.lon);
  return Number.isFinite(nm) && nm > NARNIA_SPAWN_NM;
}

export function isInvertedPair(
  a: Pick<Mission, "originIcao" | "destIcao">,
  b: Pick<Mission, "originIcao" | "destIcao">,
): boolean {
  return sameIcao(a.originIcao, b.destIcao) && sameIcao(a.destIcao, b.originIcao) && !sameIcao(a.originIcao, a.destIcao);
}

export function hydrateActiveFlightBinding(
  raw?: Partial<CompanyProfile> | Partial<ActiveFlightBinding> | null,
): ActiveFlightBinding {
  const id = typeof raw?.activeFlightId === "string" ? raw.activeFlightId.trim() : "";
  const authority = raw?.activeFlightAuthority;
  return {
    activeFlightId: id,
    activeFlightAuthority:
      authority === "desk" || authority === "megan" || authority === "sim" ? authority : undefined,
    activeFlightAt: typeof raw?.activeFlightAt === "string" ? raw.activeFlightAt : "",
    activeFlightClearedAt: typeof raw?.activeFlightClearedAt === "string" ? raw.activeFlightClearedAt : "",
  };
}

export function isHumanActiveBinding(binding: ActiveFlightBinding): boolean {
  return Boolean(binding.activeFlightId) && isHumanTicketAuthority(binding.activeFlightAuthority);
}

export function findHumanBoundMission(
  missions: readonly Mission[],
  binding?: Partial<ActiveFlightBinding> | null,
): Mission | null {
  const hydrated = hydrateActiveFlightBinding(binding);
  const open = missions.filter((m) => isOpenOpsTicket(m.status) && ticketLifecycle(m) !== "completed");
  if (isHumanActiveBinding(hydrated)) {
    const hit = open.find((m) => m.id === hydrated.activeFlightId);
    if (hit) return hit;
  }
  const stamped = open
    .filter((m) => m.activeBoundAt && isHumanTicketAuthority(m.statusAuthority))
    .sort((a, b) => String(b.activeBoundAt).localeCompare(String(a.activeBoundAt)));
  return stamped[0] ?? null;
}

export function applyMarkActive(input: {
  company: CompanyProfile;
  missions: Mission[];
  id: string;
  authority: FlightBindingAuthority;
  at: string;
}): { company: CompanyProfile; missions: Mission[] } {
  const id = input.id.trim();
  return {
    company: {
      ...input.company,
      activeFlightId: id,
      activeFlightAuthority: input.authority,
      activeFlightAt: input.at,
      activeFlightClearedAt: "",
    },
    missions: input.missions.map((m) =>
      m.id === id
        ? {
            ...m,
            activeBoundAt: input.at,
            statusAuthority: input.authority,
            statusAuthorityAt: input.at,
            lifecycle: isSimTruthLifecycle(m.lifecycle) ? m.lifecycle : "active",
          }
        : { ...m, activeBoundAt: undefined },
    ),
  };
}

export function applyClearActive(input: {
  company: CompanyProfile;
  missions: Mission[];
  at: string;
}): { company: CompanyProfile; missions: Mission[] } {
  return {
    company: {
      ...input.company,
      activeFlightId: "",
      activeFlightAuthority: undefined,
      activeFlightAt: "",
      activeFlightClearedAt: input.at,
    },
    missions: input.missions.map((m) => (m.activeBoundAt ? { ...m, activeBoundAt: undefined } : m)),
  };
}

function asMs(iso: string | null | undefined): number {
  if (!iso) return 0;
  const t = new Date(iso).getTime();
  return Number.isFinite(t) ? t : 0;
}

/**
 * Pull merge: a local human Mark Active / Clear wins over an older remote
 * guess. A newer remote human bind (another desk / Megan on Nanode) wins.
 */
export function mergeActiveFlightBinding(
  local: Partial<CompanyProfile> | null | undefined,
  remote: Partial<CompanyProfile> | null | undefined,
): ActiveFlightBinding {
  const left = hydrateActiveFlightBinding(local);
  const right = hydrateActiveFlightBinding(remote);
  const localHuman = isHumanActiveBinding(left);
  const remoteHuman = isHumanActiveBinding(right);
  const localClear = asMs(left.activeFlightClearedAt);
  const remoteBound = asMs(right.activeFlightAt);
  const localBound = asMs(left.activeFlightAt);

  if (localClear && localClear >= remoteBound && !remoteHuman) {
    return { ...left, activeFlightId: "", activeFlightAuthority: undefined, activeFlightAt: "" };
  }
  if (localClear && remoteHuman && localClear > remoteBound) {
    return { ...left, activeFlightId: "", activeFlightAuthority: undefined, activeFlightAt: "" };
  }
  if (localHuman && remoteHuman) return localBound >= remoteBound ? left : right;
  if (localHuman) return left;
  if (remoteHuman) return right;
  if (left.activeFlightId && !right.activeFlightId) return left;
  return right.activeFlightId ? right : left;
}

export function stampCompanyActiveFlight(
  company: CompanyProfile,
  binding: ActiveFlightBinding,
): CompanyProfile {
  return {
    ...company,
    activeFlightId: binding.activeFlightId,
    activeFlightAuthority: binding.activeFlightAuthority,
    activeFlightAt: binding.activeFlightAt,
    activeFlightClearedAt: binding.activeFlightClearedAt,
  };
}

export function trackRouteVote(
  mission: Pick<Mission, "originIcao" | "destIcao">,
  fused: FusedPosition | null | undefined,
  scenery: ReadonlyArray<SceneryAirport> | undefined,
): BindingVote {
  if (!fusedReady(fused) || fused?.onGround === true) return "abstain";
  const track = liveTrackDeg(fused);
  if (track == null) return "abstain";
  const origin = resolveCoords(icaoKey(mission.originIcao), [...(scenery ?? [])]);
  const dest = resolveCoords(icaoKey(mission.destIcao), [...(scenery ?? [])]);
  if (!origin || !dest) return "abstain";
  const bearing = initialTrueTrack(origin.lat, origin.lon, dest.lat, dest.lon);
  const reverse = initialTrueTrack(dest.lat, dest.lon, origin.lat, origin.lon);
  if (headingDeltaDeg(track, bearing) <= TRACK_AGREE_DEG) return "match";
  if (headingDeltaDeg(track, reverse) <= TRACK_AGREE_DEG) return "mismatch";
  return "abstain";
}

export function closingAirportsVote(
  mission: Pick<Mission, "originIcao" | "destIcao">,
  fused: FusedPosition | null | undefined,
  previous: FusedPosition | null | undefined,
  scenery: ReadonlyArray<SceneryAirport> | undefined,
  lastParkedIcao?: string | null,
): BindingVote {
  if (isNarniaGroundSpawn(fused, lastParkedIcao, scenery) || isNarniaGroundSpawn(previous, lastParkedIcao, scenery)) {
    return "abstain";
  }
  if (!fusedReady(fused) || !fusedReady(previous) || !fused || !previous) return "abstain";
  const origin = resolveCoords(icaoKey(mission.originIcao), [...(scenery ?? [])]);
  const dest = resolveCoords(icaoKey(mission.destIcao), [...(scenery ?? [])]);
  if (!origin || !dest) return "abstain";
  const prevDest = haversineNm(previous.lat, previous.lon, dest.lat, dest.lon);
  const nowDest = haversineNm(fused.lat, fused.lon, dest.lat, dest.lon);
  const prevOrigin = haversineNm(previous.lat, previous.lon, origin.lat, origin.lon);
  const nowOrigin = haversineNm(fused.lat, fused.lon, origin.lat, origin.lon);
  if (![prevDest, nowDest, prevOrigin, nowOrigin].every((n) => Number.isFinite(n))) return "abstain";
  const closingDest = prevDest - nowDest >= CLOSING_MIN_NM;
  const openingOrigin = nowOrigin - prevOrigin >= CLOSING_MIN_NM;
  const closingOrigin = prevOrigin - nowOrigin >= CLOSING_MIN_NM;
  const openingDest = nowDest - prevDest >= CLOSING_MIN_NM;
  if (closingDest && openingOrigin) return "match";
  if (closingOrigin && openingDest) return "mismatch";
  return "abstain";
}

function ofpVote(mission: Mission): BindingVote {
  const ofp = mission.ofpAttachment;
  const origin = icaoKey(ofp?.originIcao);
  const dest = icaoKey(ofp?.destIcao);
  if (!origin || !dest) return "abstain";
  if (sameIcao(origin, mission.originIcao) && sameIcao(dest, mission.destIcao)) return "match";
  return "mismatch";
}

function callsignStdVote(
  mission: Mission,
  si: ResolveCurrentFlightInput["si"],
  scheduledId: string | null,
): BindingVote {
  if (!si) return "abstain";
  const origin = icaoKey(si.originIcao);
  const dest = icaoKey(si.destIcao);
  if (origin && dest) {
    if (sameIcao(origin, mission.originIcao) && sameIcao(dest, mission.destIcao)) return "match";
    return "mismatch";
  }
  const want = normCall(mission.callsign || mission.flightNumber);
  const have = [si.callsign, si.callsignIcao].map(normCall).filter(Boolean);
  if (!want || !have.length) return "abstain";
  if (!have.some((h) => h === want || h.includes(want) || want.includes(h))) return "abstain";
  return scheduledId === mission.id ? "match" : "abstain";
}

function signalsFor(
  mission: Mission,
  input: ResolveCurrentFlightInput,
  scheduledId: string | null,
): BindingSignal[] {
  return [
    { kind: "track", vote: trackRouteVote(mission, input.fused, input.scenery), missionId: mission.id },
    {
      kind: "closing",
      vote: closingAirportsVote(mission, input.fused, input.previousFused, input.scenery, input.lastParkedIcao),
      missionId: mission.id,
    },
    { kind: "callsignStd", vote: callsignStdVote(mission, input.si, scheduledId), missionId: mission.id },
    { kind: "ofp", vote: ofpVote(mission), missionId: mission.id },
  ];
}

function juryScore(signals: BindingSignal[]): { agree: number; against: number; ready: number } {
  let agree = 0;
  let against = 0;
  for (const row of signals) {
    if (row.vote === "match") agree += 1;
    if (row.vote === "mismatch") against += 1;
  }
  return { agree, against, ready: agree + against };
}

/**
 * Shared current-flight pick. Human bind wins. Jury next. Schedule last.
 * Inverted city-pairs are rejected unless a directed signal or a human bind
 * names one side — never invent the reverse route.
 */
export function resolveCurrentFlight(input: ResolveCurrentFlightInput): CurrentFlightResolution {
  const binding = hydrateActiveFlightBinding({
    activeFlightId: input.activeFlightId ?? "",
    activeFlightAuthority: input.activeFlightAuthority ?? undefined,
  });
  const human = findHumanBoundMission(input.missions, {
    ...binding,
  });
  if (human) {
    return {
      mission: human,
      reason: "human",
      confidence: "human",
      signals: signalsFor(human, input, null),
    };
  }

  const scheduled = pickCurrentTicket(input);
  const open = input.missions.filter(
    (m) => isOpenOpsTicket(m.status) && ticketLifecycle(m) !== "completed",
  );
  const geometryOk = !isNarniaGroundSpawn(input.fused, input.lastParkedIcao, input.scenery);
  const simReady = (fusedReady(input.fused) && geometryOk) || Boolean(input.si);
  const scored = input.scoreTicket
    ? open
        .map((mission) => ({ mission, score: input.scoreTicket!(mission) }))
        .filter((row) => row.score > 0)
        .sort((a, b) => b.score - a.score)
    : [];

  let juryPick: Mission | null = null;
  let jurySignals: BindingSignal[] = [];
  if (simReady && open.length) {
    const ranked = open
      .map((mission) => {
        const signals = signalsFor(mission, input, scheduled?.id ?? null);
        const tally = juryScore(signals);
        const simScore = scored.find((row) => row.mission.id === mission.id)?.score ?? 0;
        return { mission, signals, tally, simScore };
      })
      .sort((a, b) => b.tally.agree - a.tally.agree || b.simScore - a.simScore);

    const best = ranked[0];
    if (best && best.tally.agree >= 2 && best.tally.agree > best.tally.against) {
      juryPick = best.mission;
      jurySignals = best.signals;
    } else if (best && best.tally.agree === 1 && best.tally.against === 0 && best.simScore > 0) {
      const invertRival = ranked.find(
        (row) => row.mission.id !== best.mission.id && isInvertedPair(best.mission, row.mission),
      );
      if (!invertRival) {
        juryPick = best.mission;
        jurySignals = best.signals;
      }
    } else if (scored.length === 1) {
      juryPick = scored[0]!.mission;
      jurySignals = signalsFor(juryPick, input, scheduled?.id ?? null);
    } else if (scored.length > 1) {
      const top = scored[0]!;
      const tied = scored.filter((row) => row.score === top.score).map((row) => row.mission);
      const invert = tied.find((m) => m.id !== top.mission.id && isInvertedPair(top.mission, m));
      if (invert) {
        const directed = ranked.find((row) => row.tally.agree >= 2 && row.tally.against === 0);
        juryPick = directed?.mission ?? null;
        jurySignals = directed?.signals ?? [];
      } else if (scheduled && tied.some((m) => m.id === scheduled.id)) {
        juryPick = scheduled;
      } else {
        juryPick = top.mission;
      }
    }
  }

  if (juryPick) {
    return { mission: juryPick, reason: "jury", confidence: "jury", signals: jurySignals };
  }

  const lastId = input.lastConfidentId?.trim() || "";
  const last = lastId ? open.find((m) => m.id === lastId) : null;
  if (last) {
    const invertRival = open.find((m) => m.id !== last.id && isInvertedPair(last, m));
    if (!invertRival || !scheduled || scheduled.id === last.id || !simReady) {
      return {
        mission: last,
        reason: "last_confident",
        confidence: "hold",
        signals: signalsFor(last, input, scheduled?.id ?? null),
      };
    }
  }

  if (!simReady) {
    if (scheduled) {
      const invert = open.find((m) => m.id !== scheduled.id && isInvertedPair(scheduled, m));
      if (invert) {
        return { mission: null, reason: "pending", confidence: "none", signals: [] };
      }
    }
    return {
      mission: scheduled,
      reason: scheduled ? "schedule" : "pending",
      confidence: scheduled ? "guess" : "none",
      signals: [],
    };
  }

  if (scheduled) {
    const invert = open.find((m) => m.id !== scheduled.id && isInvertedPair(scheduled, m));
    if (invert) {
      return { mission: last ?? null, reason: last ? "last_confident" : "pending", confidence: last ? "hold" : "none", signals: [] };
    }
    return {
      mission: scheduled,
      reason: "schedule",
      confidence: "guess",
      signals: signalsFor(scheduled, input, scheduled.id),
    };
  }

  return { mission: null, reason: "pending", confidence: "none", signals: [] };
}
